import { AsideMenuItem } from '../aside-menu-item';
import { AsideMenuItemWithSub } from '../aside-menu-item-with-sub';
import { Loading } from 'components/loading';
import { useAuth } from 'features/auth/contexts/auth';
import { useAppStore } from 'features/settings/contexts/settings.context';
import { Page } from 'features/shared/model';
import { FC, useMemo } from 'react';

export const AsideMenuList: FC<{}> = () => {
  const { state } = useAppStore();
  const { isGranted, currentPermissions } = useAuth();
  const isConfigurationAccessible = useMemo(() => isGranted(Page.ServiceAdvisors, 'read'), [isGranted]);
  const isPerformanceReviewAccessible = useMemo(() => isGranted(Page.PerformanceReview, 'read'), [isGranted]);
  const isUploadGrossPayPreSacrificeAccessible = useMemo(
    () => isGranted(Page.UploadGrossPayPreSacrifice, 'read'),
    [isGranted]
  );
  const canReadAttendanceLog = isGranted(Page.AttendanceLog, 'read');
  const canReadWorkingHours = isGranted(Page.VHCRecords, 'read');
  const canReadEmployees = isGranted(Page.Employees, 'read');
  const canReadEmployeesSubMenu = canReadEmployees;
  const canReadWorkPatterns = isGranted(Page.WorkPattern, 'read');
  const canReadWorkPatternsSubMenu = isGranted(Page.WorkPattern, 'read');
  const canReadApprovalsRequests = isGranted(Page.ApprovalsRequests, 'read');
  const canReadApproverSetUp = isGranted(Page.ApproverSetUp, 'read');
  const canReadHolidayRequests = isGranted(Page.HolidayRequests, 'read');
  const canReadHolidaysSubMenu = canReadApprovalsRequests || canReadApproverSetUp || canReadHolidayRequests;
  const canReadApprenticeship = isGranted(Page.Apprenticeship, 'read');
  const canReadApprenticeshipModule = isGranted(Page.Apprenticeship, 'read');

  return currentPermissions ? (
    <>
      <div className="text-gray-400 mb-7 fs-7 text-uppercase">HR</div>

      <AsideMenuItemWithSub
        to="/holiday-schedule/calendar"
        title="Attendance Log"
        icon="/media/icons/custom/attendence.svg"
      >
        {canReadAttendanceLog && <AsideMenuItem to="/attendance-log/calendar" title="Attendance Log" hasBullet />}
        {canReadWorkingHours && <AsideMenuItem to="/attendance-log/working-hours" title="Working Hours" hasBullet />}
      </AsideMenuItemWithSub>

      {canReadEmployeesSubMenu && (
        <AsideMenuItemWithSub to="/employees-management" title="Employees" icon="/media/icons/custom/profile.svg">
          {canReadEmployees && <AsideMenuItem to="/employees-management/employees" title="Employees list" hasBullet />}
          <AsideMenuItem to="/employees-management/candidates" title="Candidates" hasBullet />
        </AsideMenuItemWithSub>
      )}

      {canReadWorkPatterns && (
        <AsideMenuItemWithSub
          to="/employees-settings"
          title="Employees Settings"
          icon="/media/icons/custom/employees.svg"
        >
          {canReadWorkPatternsSubMenu && (
            <>
              <AsideMenuItem to="/employees-settings/work-patterns" title="Default Work Pattern" hasBullet />
              {isConfigurationAccessible && (
                <AsideMenuItem
                  to="/employees-settings/roles-configuration"
                  hasBullet
                  title="Job Titles Configuration"
                />
              )}

              <AsideMenuItem
                to="employees-settings/qualifications-and-bonuses"
                title="Qualifications and Bonuses"
                hasBullet
              />
              {isUploadGrossPayPreSacrificeAccessible && (
                <AsideMenuItem
                  to="/employees-settings/upload-gross-pay-pre-sacrifice"
                  hasBullet
                  title="Upload Gross Pay Pre-Sacrifice"
                />
              )}
            </>
          )}
        </AsideMenuItemWithSub>
      )}

      {canReadHolidaysSubMenu && (
        <AsideMenuItemWithSub to="/holidays" title="Holidays" icon="/media/icons/custom/calendar.svg">
          {canReadApprovalsRequests && (
            <AsideMenuItem
              to="/holidays/approvals"
              title="Approvals (Requests)"
              hasBullet
              extraInfo={{ text: state.notApproved.toString() }}
            />
          )}
          {canReadApproverSetUp && <AsideMenuItem to="/holidays/approvers" title="Approver Setup" hasBullet />}
          {canReadHolidayRequests && <AsideMenuItem to="/holidays/request" title="Holiday Request" hasBullet />}
        </AsideMenuItemWithSub>
      )}

      {isPerformanceReviewAccessible && (
        <AsideMenuItem
          to="/performance-review"
          icon="/media/icons/custom/chart-line-star.svg"
          title="Performance Review"
        />
      )}

      {canReadApprenticeship && (
        <AsideMenuItemWithSub to="/training-plans" title="Training Plans" icon="/media/icons/custom/apprenticeship.svg">
          {canReadApprenticeshipModule && (
            <>
              <AsideMenuItem to="/training-plans/modules" title="Manage modules" hasBullet />
              <AsideMenuItem to="/training-plans/questions" title="My Training Program" hasBullet />
              <AsideMenuItem to="/training-plans/check-lists" title="Trainee Check List" hasBullet />
            </>
          )}
        </AsideMenuItemWithSub>
      )}
    </>
  ) : (
    <Loading />
  );
};
